import moment from "moment";

// Check if the current date is within the financial year
export const isCurrentFinancialYear = (item) => {
  const currentDate = new Date();
  return (
    currentDate >= new Date(item?.start_date) &&
    currentDate <= new Date(item?.end_date)
  );
};

export const getDateValue = (val, format = "DD-MM-YYYY") => {
  return val ? moment(val).format(format) : "-";
};

export const QUERY_PARAMS = (params = {}) =>
  Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");

export const replaceSpaces = (val) => {
  return val
    ? val
        .toLowerCase()
        .replace(/%20| /g, "_")
        .replace(/[^a-z0-9_]/g, "")
    : "";
};

export const findMatchingPath = (route, currentPath) => {
  if (route?.length > 0) {
    for (let i = 0; i < route?.length; i++) {
      if (route[i].path === currentPath) {
        return route[i];
      }
      if (route[i].submodules?.length > 0) {
        const submoduleResult = findMatchingPath(
          route[i].submodules,
          currentPath
        );
        if (submoduleResult) {
          return submoduleResult;
        }
        for (let j = 0; j < route[i].modulesOfSubModule; j++) {
          if (route[i].modulesOfSubModule[j].path === currentPath) {
            return route[i].modulesOfSubmodule[j];
          }
        }
      }
    }
  }

  return null;
};

// Returns value rounded to max 2 decimal places; empty string if no value
export const twoDecimal = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return parseFloat(num.toFixed(2));
};

// Returns value rounded to max 3 decimal places; empty string if no value
export const threeDecimal = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return parseFloat(num.toFixed(3));
};

// Limits an input string to at most 2 decimal places (used inside onChange handlers)
export const limitToTwoDecimal = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const str = String(val);
  const match = str.match(/^-?\d*\.?\d{0,2}/);
  return match ? match[0] : "";
};

// onKeyDown handler: allow only digits 0-9 (no decimal, no sign).
// Named keys (Backspace, Delete, arrows, Tab, Home/End…) have e.key longer
// than one character and must always pass — these handlers are wired to
// onKeyDown, where Backspace DOES fire; blocking it froze every money input
// ("amount daal ke edit nahi ho raha").
export const onlyDigitKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key.length > 1) return;
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

// onKeyDown handler: allow digits 0-9 and a single decimal point.
export const onlyDecimalKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  if (e.key.length > 1) return;
  const current = (e.target && e.target.value) || "";
  if (e.key === "." && !current.includes(".")) return;
  if (!/^\d$/.test(e.key)) {
    e.preventDefault();
  }
};

// onPaste handler: block paste if clipboard contains anything other than digits
export const handleDigitPaste = (e) => {
  const pasted = (e.clipboardData || window.clipboardData).getData("text");
  if (!/^\d+$/.test(pasted)) {
    e.preventDefault();
  }
};

// onPaste handler: block paste if clipboard isn't a valid decimal number
export const handleDecimalPaste = (e) => {
  const pasted = (e.clipboardData || window.clipboardData).getData("text");
  if (!/^\d*\.?\d*$/.test(pasted) || pasted === "" || pasted === ".") {
    e.preventDefault();
  }
};

// Amounts always render to 2 decimals — this used to print "₹1,234" (and a
// bare "₹0" for anything falsy), so the same value looked different depending
// on which helper a screen happened to use. Now identical to formatAmountINR.
export const formatNumberToINR = (number = 0) => {
  const n = Number(number) || 0;
  const body = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? "-" : ""}₹${body}`;
};

// An amount for a table cell, whatever shape the API sends it in (1234.5,
// "1234.5", "₹ 1234.5"): always "₹1,234.50". Blank stays "-".
export const formatAmountCell = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return "-";
  const n = Number(String(value).replace(/[^0-9.-]/g, ""));
  return Number.isFinite(n) ? formatNumberToINR(n) : String(value);
};

// ── An amount written out, Indian style ─────────────────────────────────────
// For a payslip, where the figure has to be readable as a sentence as well as a
// number. Lakh and crore, not million — "₹1,20,000.00" reads as "One Lakh
// Twenty Thousand Rupees Only", which is what anybody here expects to see on a
// receipt. Paise are named separately when there are any.
const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = [
  "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty",
  "Ninety",
];

const under1000 = (n) => {
  if (!n) return "";
  if (n < 20) return ONES[n];
  if (n < 100)
    return `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ""}`;
  return `${ONES[Math.floor(n / 100)]} Hundred${
    n % 100 ? ` ${under1000(n % 100)}` : ""
  }`;
};

export const amountInWords = (value = 0) => {
  const n = Math.abs(Number(value) || 0);
  const whole = Math.floor(n);
  const paise = Math.round((n - whole) * 100);
  if (!whole && !paise) return "Zero Rupees Only";

  // Indian grouping: crore, lakh, thousand, then the last three digits.
  const parts = [];
  const push = (count, label) => {
    if (count) parts.push(`${under1000(count)} ${label}`);
  };
  push(Math.floor(whole / 10000000), "Crore");
  push(Math.floor((whole % 10000000) / 100000), "Lakh");
  push(Math.floor((whole % 100000) / 1000), "Thousand");
  const last = whole % 1000;
  if (last) parts.push(under1000(last));

  const rupees = parts.length ? `${parts.join(" ")} Rupees` : "";
  const p = paise ? `${under1000(paise)} Paise` : "";
  const sign = Number(value) < 0 ? "Minus " : "";
  return `${sign}${[rupees, p].filter(Boolean).join(" and ")} Only`;
};

// ── Fund / Stock QUANTITY formatting — always EXACTLY 3 decimals ────────────
// Quantities, not money: a fund/stock line can legitimately be 2.235 units,
// so quantities keep 3 decimals. Amounts do NOT — see formatAmountINR below.
//   to3(5) → "5.000"   to3(2.5) → "2.500"   to3(2.98765) → "2.988"
export const to3 = (n) => (Number(n) || 0).toFixed(3);

// ── AMOUNT formatting — "₹" + Indian grouping + exactly 2 decimals ─────────
// Every amount-related value in the app (amount / rate / balance / bill /
// paid / outstanding / salary) renders to 2 decimals. This used to print 3
// ("₹1,234.500"), which read as a quantity rather than money.
//   formatAmountINR(1234.5) → "₹1,234.50"   formatAmountINR(-5) → "-₹5.00"
export const formatAmountINR = (number = 0) => {
  const n = Number(number) || 0;
  const body = Math.abs(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${n < 0 ? "-" : ""}₹${body}`;
};

export const getImageSrc = (selectedFile, fallbackUrl) => {
  if (
    selectedFile &&
    selectedFile.type &&
    selectedFile.type.startsWith("image/")
  ) {
    return URL.createObjectURL(selectedFile);
  }

  return `${process.env.REACT_APP_API_URL}/${fallbackUrl}` || "";
};

export const serialNumber = (pageNo, pageSize) => {
  const start = (+pageNo - 1) * +pageSize + 1;
  return Array.from({ length: +pageSize }, (_, index) => start + index);
};

// download csv
export const convertToCSV = (data) => {
  const csv = data.map((row) => row.join(",")).join("\n");
  return "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
};

export const DOWNLOAD_CSV_FILE = (data, filename = "data.csv") => {
  const csvData = convertToCSV(data);
  const downloadLink = document.createElement("a");
  downloadLink.setAttribute("href", csvData);
  downloadLink.setAttribute("download", filename);
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
};

export const DOWNLOAD_FILE_WITH_BACKEND = (fileUrl) => {
  const element = document.createElement("a");
  element.href = `${process.env.REACT_APP_API_URL}/sample_file/${fileUrl}`;
  element.download = fileUrl;
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
};

export const preFillFormData = (e, setFieldValue, data) => {
  if (!e) {
    data.forEach((itm) => {
      setFieldValue(itm.to, "");
    });
  } else {
    data.forEach((itm) => {
      setFieldValue(itm.to, e[itm.fill]);
    });
  }
};

export const TOGGLE_ROW_HELPER = (prevCollapsedRows, index) => {
  const isCollapsed = prevCollapsedRows?.includes(index);
  if (isCollapsed) {
    return prevCollapsedRows?.filter((rowIndex) => rowIndex !== index);
  } else {
    return [...prevCollapsedRows, index];
  }
};
